import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../config/URL";
import {
  createTheme,
  IconButton,
  Menu,
  MenuItem,
  ThemeProvider,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import GlobalDelete from "../../../components/common/GlobalDelete";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../../utility/mrtColumnPersistence";

const MODULE_KEY = "LEAVEREQUEST";

const Leave = () => {
  const [datas, setDatas] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const storedScreens = JSON.parse(localStorage.getItem("screens") || {});
  const canIndex = Boolean(storedScreens?.leaveRequestIndex);
  const canRead = Boolean(storedScreens?.leaveRequestRead);
  const canCreate = Boolean(storedScreens?.leaveRequestCreate);
  const canUpdate = Boolean(storedScreens?.leaveRequestUpdate);
  const canDelete = Boolean(storedScreens?.leaveRequestDelete);
  const showActionColumn = canUpdate || canDelete;
  const defaultColumnVisibility = {
    createdBy: false,
    createdAt: false,
    updatedBy: false,
    updatedAt: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );
  const userId = localStorage.getItem("userId");

  const getData = async () => {
    try {
      const response = await api.get(`/getUserLeaveRequestByUserId/${userId}`);
      setDatas(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getData();
  }, []);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      {
        accessorKey: "leaveStatus",
        header: "Leave Status",
        Cell: ({ row }) =>
          row.original.leaveStatus === "APPROVED" ? (
            <span className="badge bg-success fw-light">Approved</span>
          ) : row.original.leaveStatus === "REJECTED" ? (
            <span className="badge bg-danger fw-light">Rejected</span>
          ) : (
            <span className="badge bg-warning fw-light">Pending</span>
          ),
      },
      { accessorKey: "fromDate", header: "From Date" },
      { accessorKey: "toDate", header: "To Date" },
      { accessorKey: "leaveType", header: "Leave Type" },
      { accessorKey: "noOfDays", header: "No Of Days" },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      { accessorKey: "updatedBy", header: "Updated By" },
    ];

    if (!showActionColumn) return baseColumns;

    const actionColumn = {
      id: "actions",
      header: "",
      size: 20,
      enableSorting: false,
      Cell: ({ row }) =>
        row.original.leaveStatus === "PENDING" ? (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
              setSelectedId(row.original.id);
            }}
          >
            <MoreVertIcon />
          </IconButton>
        ) : (
          <div style={{ width: 24 }} />
        ),
    };

    // ✅ Action column ALWAYS second
    return [baseColumns[0], actionColumn, ...baseColumns.slice(1)];
  }, [showActionColumn]);

  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
      // Switch (Toggle button) customization
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&.Mui-disabled .MuiSwitch-track": {
              backgroundColor: "#f5e1d0", // Track color when disabled
              opacity: 1, // Ensures no opacity reduction
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: "#eb862a", // Thumb (circle) color when disabled
            },
          },
          track: {
            backgroundColor: "#e0e0e0", // Default track color
          },
          thumb: {
            color: "#eb862a", // Default thumb color
          },
          switchBase: {
            "&.Mui-checked": {
              color: "#eb862a", // Thumb color when checked
            },
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#eb862a", // Track color when checked
            },
          },
        },
      },
    },
  });

  const handleMenuClose = () => setMenuAnchor(null);

  return (
    <div className="container-fluid my-4 center">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          &nbsp;Staffing
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          &nbsp;Leave Request
        </li>
      </ol>
      <div className="card">
        <div
          className="mb-3 d-flex justify-content-between align-items-center p-1"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="d-flex">
              <div className="dot active"></div>
            </div>
            <span className="me-2 text-muted">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Leave Request
              </span>
            </span>
          </div>
        </div>
        <div className="d-flex justify-content-end align-items-center">
          {canCreate && (
            <Link to="/leave/add">
              <button
                type="button"
                className="btn btn-button btn-sm me-2"
                style={{ fontWeight: "600px !important" }}
              >
                &nbsp; Add &nbsp;&nbsp; <i className="bx bx-plus"></i>
              </button>
            </Link>
          )}
        </div>
        <div className="d-flex justify-content-center align-items-center">
          <div className="col-md-6 col-12 p-2">
            Employee Name &nbsp; &nbsp;: &nbsp; &nbsp;
            {datas.employeeName}
          </div>
          <div className="col-md-6 col-12 p-2">
            Leave Limit &nbsp; &nbsp;: &nbsp; &nbsp;{datas.leaveLimit}
          </div>
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable
                columns={columns}
                data={datas?.employeeData}
                state={{ columnVisibility }}
                onColumnVisibilityChange={(updater) => {
                  const newVisibility =
                    typeof updater === "function"
                      ? updater(columnVisibility)
                      : updater;

                  setColumnVisibility(newVisibility);
                  saveColumnPreference(MODULE_KEY, newVisibility);
                }}
                enableColumnActions={true}
                enableColumnFilters={false}
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                initialState={{
                  pagination: { pageSize: 50, pageIndex: 0 },
                }}
                muiTableBodyRowProps={({ row }) => ({
                  onClick: canRead
                    ? () => navigate(`/leave/view/${row.original.id}`)
                    : undefined,
                  style: { cursor: canRead ? "pointer" : "default" },
                })}
              />
            </ThemeProvider>

            <Menu
              id="action-menu"
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
              disableScrollLock
            >
              {canUpdate && (
                <MenuItem
                  onClick={() => navigate(`/leave/edit/${selectedId}`)}
                  className="text-start mb-0 menuitem-style"
                >
                  Edit
                </MenuItem>
              )}

              {canDelete && (
                <MenuItem>
                  <GlobalDelete
                    path={`/deleteUserLeaveRequest/${selectedId}`}
                    onDeleteSuccess={getData}
                    onOpen={handleMenuClose}
                  />
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};

export default Leave;
